import dayjs from 'dayjs';
import { always, curry, identity, ifElse, mergeAll, pick, values } from 'ramda';
import { MOMENT_DATE_TIME_MASK, MOMENT_DEFAULT_DATE_MASK, MOMENT_DEFAULT_LINGUAL_DATE_TIME_MASK, WTS_MOMENT_DATE_MASK, WTS_MOMENT_SHORT_DATE_MASK } from 'src/constants/dayjs';
import { INITIAL_TIMESPAN } from 'src/constants/ui';
import { I18n } from 'src/translations/i18n';
export const MONDAY_TO_SUNDAY_MOMENT_DAYS_SEQUENCE = [1, 2, 3, 4, 5, 6, 0];
export const CUSTOM_PERIOD_KEY = 'CustomPeriod';
export const defaultFormat = MOMENT_DEFAULT_DATE_MASK;
export const shortFormat = 'D.M.YYYY';
export function isDST(date) {
    // Compare offsets in January vs. June
    const januaryOffset = date.clone().month(0).utcOffset();
    const juneOffset = date.clone().month(5).utcOffset();
    // If the June offset is smaller, that means June is in DST for this region
    const isRegionDSTInJune = juneOffset < januaryOffset;
    // If region is DST in June, then offset == juneOffset => DST right now
    // Otherwise, offset == januaryOffset => DST right now (Southern Hemisphere)
    if (isRegionDSTInJune) {
        return date.utcOffset() === juneOffset;
    }
    else {
        return date.utcOffset() === januaryOffset;
    }
}
export const getDiff = (end, start) => {
    const clonedStart = start.clone();
    let clonedEnd = end ? end.clone() : dayjs();
    // todo TLL request Kella keeramine
    if (!isDST(clonedStart) && isDST(clonedEnd)) {
        clonedEnd = clonedEnd.subtract(1, 'hour');
    }
    if (isDST(clonedStart) && !isDST(clonedEnd)) {
        clonedEnd = clonedEnd.add(1, 'hour');
    }
    return clonedEnd.diff(clonedStart);
};
export const duration = (endMoment, startMoment) => {
    return dayjs.duration(getDiff(endMoment, startMoment));
};
export const getPeriodDurationInTimeFormat = (start, end) => {
    const diff = getDiff(end || dayjs(), start);
    let hours = Math.floor(diff / 1000 / 60 / 60);
    let minutes = Math.round((diff / 1000 / 60) % 60);
    if (minutes >= 60) {
        hours += 1;
        minutes -= 60;
    }
    return `${hours}:${minutes < 10 ? `0${minutes}` : minutes}`;
};
export const getDayJobLimits = (start, end, date) => {
    const dayMomentObject = date ? dayjs(date) : dayjs();
    const dayStart = dayMomentObject.clone().startOf('day');
    const dayEnd = dayMomentObject.clone().endOf('day');
    const jobStart = dayjs(start, WTS_MOMENT_DATE_MASK);
    const jobEnd = end ? dayjs(end, WTS_MOMENT_DATE_MASK) : dayjs();
    const limitedJobStart = jobStart.isBefore(dayStart) ? dayStart : jobStart;
    const limitedJobEnd = jobEnd.isAfter(dayEnd) ? dayEnd : jobEnd;
    return ({
        jobStart: limitedJobStart,
        // An unfinished job (no EndDate) ends "now", which can be earlier than the viewed day.
        // Without this guard the duration turns negative (broken day total and timeline bar width).
        jobEnd: limitedJobEnd.isBefore(limitedJobStart) ? limitedJobStart : limitedJobEnd
    });
};
export const getPeriods = (thisDay = dayjs(), includeLegacy = false, includeQuarters = false) => {
    const lastDay = dayjs(thisDay).subtract(1, 'days');
    const lastWeek = dayjs(thisDay).subtract(1, 'week');
    const lastMonth = dayjs(thisDay).subtract(1, 'months');
    const thisYear = dayjs(thisDay).startOf('year');
    const lastYear = dayjs(thisDay).subtract(1, 'years');
    const periods = {
        today: {
            startDate: dayjs(thisDay).startOf('day'),
            endDate: dayjs(thisDay).endOf('day'),
            translationKey: 'today'
        },
        lastDay: {
            startDate: dayjs(lastDay).startOf('day'),
            endDate: dayjs(lastDay).endOf('day'),
            translationKey: 'yesterday'
        },
        thisWeek: {
            startDate: dayjs(thisDay).startOf('isoWeek'),
            endDate: dayjs(thisDay).endOf('isoWeek'),
            translationKey: 'ThisWeek'
        },
        lastWeek: {
            startDate: dayjs(lastWeek).startOf('isoWeek'),
            endDate: dayjs(lastWeek).endOf('isoWeek'),
            translationKey: 'LastWeek'
        },
        thisMonth: {
            startDate: dayjs(thisDay).startOf('month'),
            endDate: dayjs(thisDay).endOf('month'),
            translationKey: 'ThisMonth'
        },
        lastMonth: {
            startDate: dayjs(lastMonth).startOf('month'),
            endDate: dayjs(lastMonth).endOf('month'),
            translationKey: 'LastMonth'
        },
        thisYear: {
            startDate: dayjs(thisDay).startOf('year'),
            endDate: dayjs(thisDay).endOf('year'),
            translationKey: 'ThisYear'
        },
        lastYear: {
            startDate: dayjs(lastYear).startOf('year'),
            endDate: dayjs(lastYear).endOf('year'),
            translationKey: 'LastYear'
        }
    };
    if (!includeLegacy && !includeQuarters)
        return periods;
    // Keep legacy to not break dashboard widgets render
    const periodsLegacy = {
        last7Days: {
            startDate: dayjs(thisDay).subtract(6, 'days'),
            endDate: dayjs(thisDay),
            translationKey: 'Last7Days'
        },
        last30Days: {
            startDate: dayjs(thisDay).subtract(29, 'days'),
            endDate: dayjs(thisDay),
            translationKey: 'Last30Days'
        }
    };
    const quarters = {
        thisQuarter: {
            startDate: dayjs(thisDay).startOf('quarter'),
            endDate: dayjs(thisDay).endOf('quarter'),
            translationKey: 'ThisQuarter'
        },
        lastQuarter: {
            startDate: dayjs(thisYear).endOf('year').startOf('quarter'),
            endDate: dayjs(thisYear).endOf('year').endOf('quarter'),
            translationKey: 'LastQuarter'
        }
    };
    return mergeAll([
        periods,
        includeLegacy ? periodsLegacy : {},
        includeQuarters ? quarters : {}
    ]);
};
export const getDefaultPeriods = () => values(pick(['today', 'lastDay', 'thisWeek', 'lastWeek', 'thisMonth', 'lastMonth'], getPeriods()));
export function periodToRelativeDate(start, end) {
    const isPeriodSame = curry(periodEquals)({ startDate: start, endDate: end });
    const periods = getPeriods(dayjs(), false, true);
    const period = Object.keys(periods).find((key) => isPeriodSame(periods[key]));
    return period || CUSTOM_PERIOD_KEY;
}
export const periodEquals = (period1, period2, precision = 'day') => {
    const { startDate: startDate1, endDate: endDate1 } = period1;
    const { startDate: startDate2, endDate: endDate2 } = period2;
    const startEq = dayjs(startDate1).isSame(dayjs(startDate2), precision);
    const endEq = dayjs(endDate1).isSame(dayjs(endDate2), precision);
    return startEq && endEq;
};
export const getPeriodVerbose = ({ startDate, endDate }) => {
    const key = periodToRelativeDate(startDate, endDate);
    switch (key) {
        case 'today':
        case 'lastDay': {
            return dayjs(startDate).format(MOMENT_DEFAULT_DATE_MASK);
        }
        case 'thisWeek':
        case 'lastWeek': {
            return dayjs(startDate).format('w - YYYY');
        }
        case 'thisMonth':
        case 'lastMonth': {
            return dayjs(startDate).format('MMMM YYYY');
        }
        case 'thisYear':
        case 'lastYear': {
            return dayjs(startDate).format('YYYY');
        }
        case 'thisQuarter':
        case 'lastQuarter': {
            return `${dayjs(startDate).format('MMM')}-${dayjs(endDate).format('MMM')}`;
        }
        default: {
            return `${dayjs(startDate).format(MOMENT_DEFAULT_DATE_MASK)} - ${dayjs(endDate).format(MOMENT_DEFAULT_DATE_MASK)}`;
        }
    }
};
export const getFormattedPeriod = ({ StartDate, EndDate }, format = WTS_MOMENT_DATE_MASK) => {
    return EndDate
        ? `${dayjs(StartDate).format(format)} - ${dayjs(EndDate).format(format)}`
        : `${dayjs(StartDate).format(format)} - ${I18n.t('Now')}`;
};
// formats period to request period string
export const formatPeriod = ({ startDate, endDate }) => {
    const start = dayjs(startDate).format(WTS_MOMENT_DATE_MASK);
    const end = dayjs(endDate).format(WTS_MOMENT_DATE_MASK);
    return `${start},${end}`;
};
export const formatNullablePeriod = ({ startDate, endDate }) => {
    const start = startDate ? dayjs(startDate).format(WTS_MOMENT_DATE_MASK) : '';
    const end = endDate ? dayjs(endDate).format(WTS_MOMENT_DATE_MASK) : '';
    return `${start},${end}`;
};
export const convertTime = (time, acceptZeros = false) => {
    let dateObj = '';
    if (!acceptZeros && (time === '00:00:00' || time == null)) {
        return ' ';
    }
    else if (dayjs(time, WTS_MOMENT_DATE_MASK, true).isValid()) {
        dateObj = [dayjs(time).format('DD.MMM.YYYY HH:mm:ss'), dayjs(time).format('HH:mm:ss')];
        return dateObj;
    }
    else {
        // dayjs(time,"HH:mm:ss", true).isValid()   this is not working with time over 24 h , like 24:05:10
        return time.slice(0, -3); // remove seconds
    }
};
const addZero = (i) => {
    let str = i.toString();
    if (i < 10) {
        str = `0${i}`;
    }
    return str;
};
export const formatTime = (totalMinutes) => {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = addZero(Math.floor(totalMinutes % 60));
    return `${hours}:${minutes}`;
};
export const validateDate = (date) => {
    const d = dayjs(date);
    if (d.isValid())
        return d;
    return null;
};
// returns date if valid, else - now
export const dateOrNow = (now = dayjs()) => ifElse((date) => date.isValid(), identity, always(now));
// returns date if it's after start, else start
export const dateOrStartBorder = curry((start, date) => ifElse((d) => d.isBefore(start), always(start), identity)(date));
// returns date if it's before end, else end
export const dateOrEndBorder = curry((end, date) => ifElse((d) => d.isAfter(end), always(end), identity)(date));
export const arePeriodsIntersects = (period, otherPeriod) => {
    const start = dayjs(period.startDate);
    const end = dayjs(period.endDate);
    const s = dayjs(otherPeriod.startDate);
    const e = dayjs(otherPeriod.startDate);
    // start before, end before
    const sbeb = start.isBefore(s) && end.isBefore(e);
    // start before, end after
    const sbea = start.isBefore(s) && end.isAfter(e);
    // start after, end before
    const saeb = start.isAfter(s) && end.isBefore(e);
    // start after end after
    const saea = start.isAfter(s) && end.isAfter(e);
    return sbeb || sbea || saeb || saea;
};
export const getShortTime = (time) => dayjs(time).format('ll');
export const getLongTime = (time) => dayjs(time).format('lll');
export const timeSpanToTimePickerFormat = (time) => {
    if (!time)
        time = INITIAL_TIMESPAN;
    const [hours, minutes] = time.split(':');
    return dayjs().hour(+hours).minute(+minutes);
};
export const formatDayjsToTimeSpan = (value) => (value ? value.format('HH:mm') : '00:00');
export const formatWtsTimeToDefaultDateTime = (time) => (dayjs(time).locale('et').format(MOMENT_DEFAULT_LINGUAL_DATE_TIME_MASK));
export const formatDatesToWtsPeriod = (start, end, mask = WTS_MOMENT_SHORT_DATE_MASK) => (`${start.format(mask)},${end.format(mask)}`);
export const getRequestPeriod = (date) => {
    const { startDate, endDate } = getPeriod(date);
    return formatDatesToWtsPeriod(startDate, endDate);
};
export const getInitialDateRangePickerPeriod = () => ({
    startDate: dayjs().subtract(1, 'months').startOf('month'),
    endDate: dayjs().add(1, 'months').endOf('month')
});
export const getPeriod = (date) => {
    const { date: periodDate, periodType } = date;
    const type = periodType[0];
    if (type === '2weeks') {
        return ({
            startDate: dayjs(periodDate).startOf('week'),
            endDate: dayjs(periodDate).add(1, 'week').endOf('week')
        });
    }
    else if (type === 'custom') {
        return ({
            startDate: dayjs(periodDate).startOf('day'),
            endDate: dayjs(date.toDate).endOf('day')
        });
    }
    return ({
        startDate: dayjs(periodDate).startOf(type),
        endDate: dayjs(periodDate).endOf(type)
    });
};
export const isInPeriod = (period, date) => date.isSameOrAfter(period[0]) && date.isSameOrBefore(period[1]);
export const numberToTimeFormat = (number) => {
    const hours = Math.floor(number);
    const minutes = Number(((number % 1) * 60).toFixed(0));
    const minutesString = minutes < 10 ? `0${minutes}` : minutes;
    return `${hours}:${minutesString}`;
};
// eslint-disable-next-line consistent-return
export const getPeriodDurationNumber = (start, end, type = 'hours') => {
    const diff = getDiff(end, start);
    switch (type) {
        case 'days':
            return dayjs.duration(diff).asDays();
        case 'hours':
            return dayjs.duration(diff).asHours();
        case 'minutes':
            return dayjs.duration(diff).asMinutes();
    }
};
export const checkPeriodsOverlap = (period1, period2, granularity, // as in Dayjs
inclusivity // as in Dayjs
// https://momentjscom.readthedocs.io/en/latest/moment/05-query/06-is-between/
) => {
    const [startDate1, endDate1] = period1;
    const [startDate2, endDate2] = period2;
    return (startDate1.isBetween(...period2, granularity, inclusivity)
        || endDate1.isBetween(...period2, granularity, inclusivity)
        || startDate2.isBetween(...period1, granularity, inclusivity)
        || endDate2.isBetween(...period1, granularity, inclusivity));
};
export const periodStringToDayjsRange = (period) => {
    const [startDate, endDate] = period.split(',');
    return [dayjs(startDate), dayjs(endDate)];
};
export const dayjsRangeToPeriodString = (period) => {
    const [startDate, endDate] = period;
    return `${startDate.format(WTS_MOMENT_DATE_MASK)},${endDate.format(WTS_MOMENT_DATE_MASK)}`;
};
export const endDateInclusiveToExclusive = (date) => {
    const djsDate = dayjs(date);
    const candidate = djsDate.add(1, 'day').startOf('day');
    if (dayjs.isDayjs(date)) {
        return candidate;
    }
    else {
        return candidate.format(WTS_MOMENT_DATE_MASK);
    }
};
export const periodStringInclusiveToExclusive = (period) => {
    const [startDate, endDate] = period.split(',');
    const end = endDateInclusiveToExclusive(endDate);
    return `${startDate},${end}`;
};
export const splitPeriodOntoFullMonths = (period) => {
    const [startDate, endDate] = period;
    const months = endDate.clone().startOf('month').diff(startDate.clone().startOf('month'), 'months', true);
    return Array.from({ length: months + 1 }, (_, i) => {
        const start = startDate.clone().add(i, 'months').startOf('month');
        const end = start.clone().endOf('month');
        return [start, end];
    });
};
export const getPeriodsArray = (from, to, dateType = 'month') => {
    const fromMoment = (dayjs.isDayjs(from) ? from : dayjs(from)).clone().startOf(dateType);
    const toMoment = (dayjs.isDayjs(to) ? to : dayjs(to)).clone().startOf(dateType);
    const start = fromMoment.isSameOrBefore(toMoment) ? fromMoment : toMoment;
    const end = fromMoment.isAfter(toMoment) ? fromMoment : toMoment;
    let temporary = start;
    const res = [];
    do {
        res.push(temporary);
        temporary = temporary.clone().add(1, dateType);
    } while (temporary.isSameOrBefore(end));
    return res;
};
export const isPeriodsCrossed = (limits, start, end) => (start.isAfter(limits[0]) && start.isBefore(limits[1]))
    || (end.isAfter(limits[0]) && end.isBefore(limits[1]))
    || (start.isBefore(limits[0]) && end.isAfter(limits[1]));
export const formatTimeWithoutSeconds = (time) => {
    if (!time) {
        return '00:00';
    }
    const timeString = `${time}`;
    const splited = timeString.split(':') || [];
    if (splited.length < 2) {
        return timeString || '';
    }
    const [hours, minutes] = splited;
    return `${hours}:${minutes}`;
};
export const getMinMax = (dates) => {
    const min = dayjs.min(dates.map((d) => dayjs(d)));
    const max = dayjs.max(dates.map((d) => dayjs(d)));
    return [min, max];
};
export const isWeekPeriod = (start, end) => {
    const startDate = dayjs(start, 'DD-MM-YYYY').startOf('day');
    const endDate = dayjs(end, 'DD-MM-YYYY').endOf('day');
    const isStartFromMonday = dayjs(start, 'DD-MM-YYYY').startOf('isoWeek')
        .format(MOMENT_DATE_TIME_MASK) === startDate.format(MOMENT_DATE_TIME_MASK);
    const isFinishOnSaturday = dayjs(end, 'DD-MM-YYYY').endOf('isoWeek')
        .format(MOMENT_DATE_TIME_MASK) === endDate.format(MOMENT_DATE_TIME_MASK);
    const isSameWeek = startDate.week() === endDate.week();
    return isStartFromMonday && isFinishOnSaturday && isSameWeek;
};
export const is2WeeksPeriod = (start, end) => {
    const startDate = dayjs(start, 'DD-MM-YYYY').startOf('day');
    const endDate = dayjs(end, 'DD-MM-YYYY').endOf('day');
    const isStartFromMonday = dayjs(start, 'DD-MM-YYYY').startOf('isoWeek')
        .format(MOMENT_DATE_TIME_MASK) === startDate.format(MOMENT_DATE_TIME_MASK);
    const isFinishOnSaturday = dayjs(end, 'DD-MM-YYYY').endOf('isoWeek')
        .format(MOMENT_DATE_TIME_MASK) === endDate.format(MOMENT_DATE_TIME_MASK);
    const is2Weeks = endDate.week() - startDate.week() === 1;
    return isStartFromMonday && isFinishOnSaturday && is2Weeks;
};
export const getDurationBetweenTimeSpans = (start, end) => {
    // Create durations from timespans - this automatically handles HH:mm, HH:mm:ss, and D.HH:mm:ss formats
    const startDuration = dayjs.duration(start);
    const endDuration = dayjs.duration(end);
    // Convert durations to milliseconds for comparison and calculation
    const startMs = startDuration.asMilliseconds();
    const endMs = endDuration.asMilliseconds();
    let diffMs;
    // Handle midnight crossing: if end time is less than start time,
    // it means the period crosses midnight (e.g., 22:00 to 06:00 next day)
    if (endMs < startMs) {
        // Add 24 hours (86400000 ms) to end time to account for next day
        diffMs = (endMs + 86_400_000) - startMs;
    }
    else {
        // Normal case: end time is after start time on the same day
        diffMs = endMs - startMs;
    }
    // Convert back to duration and format as HH:mm:ss
    const resultDuration = dayjs.duration(diffMs);
    return resultDuration.format('HH:mm:ss');
};
